import { ROUTES } from '../app.constants'
import i18n from '@hapana/shared/web/i18n'

// Helper to build site-level path
const buildSitePath = (path, siteId) => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path

  if (!siteId) {
    // If no siteId, redirect to location-selection with the intended path
    return `/location-selection?redirect=${encodeURIComponent(cleanPath)}`
  }

  return `/s/${siteId}/${cleanPath}`
}

// Helper to build corporate-level path
const buildCorporatePath = (path) => {
  const cleanPath = path.startsWith('/') ? path.slice(1) : path
  return `/c/${cleanPath}`
}

export const getCuratedCommands = (siteId) => {
  return [
    // Site-level navigation
    {
      id: 'nav-dashboard',
      name: 'Dashboard',
      keywords: 'dashboard home welcome brands',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildSitePath(ROUTES.dashboard.path, siteId),
    },
    // Corporate-level navigation
    {
      id: 'nav-reports',
      name: 'Reports',
      keywords: 'exports reports generate data download',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildCorporatePath(ROUTES.reports.path),
    },
    // Site-level navigation
    {
      id: 'nav-clients',
      name: 'Clients',
      keywords: 'clients members users customers',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    // Site-level navigation
    {
      id: 'nav-mobile-setup',
      name: 'Mobile Setup',
      keywords: 'mobile app setup customization branding',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    // Corporate-level navigation
    {
      id: 'nav-control',
      name: 'Control Center',
      keywords: 'control center settings brand preferences',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    // Corporate-level navigation
    {
      id: 'nav-user-access',
      name: 'User Access',
      keywords: 'user access permissions roles policies groups',
      section: i18n.t('core:kbar.sections.navigation'),
      perform: () => buildCorporatePath(ROUTES.userAccess.path),
    },

    // Export Reports - Corporate-level (redirect to Reports page)
    {
      id: 'export-reports',
      name: 'Export Reports',
      keywords:
        'location reports key performance all members net revenue failed payment projected revenue sales membership attendance',
      section: i18n.t('core:kbar.sections.reportsExports'),
      perform: () => buildCorporatePath(ROUTES.reports.path),
    },

    // Client Management - Site-level
    {
      id: 'client-add',
      name: 'Add Client',
      keywords: 'add client new member customer',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'client-manage',
      name: 'Manage Clients',
      keywords: 'manage clients edit members',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'client-active',
      name: 'Active Clients',
      keywords: 'active clients current members',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'client-intro',
      name: 'Intro Clients',
      keywords: 'intro clients trial new leads',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'client-alumni',
      name: 'Alumni Clients',
      keywords: 'alumni clients former past members',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'client-corporate-lookup',
      name: 'Corporate Lookup',
      keywords: 'corporate lookup business clients',
      section: i18n.t('core:kbar.sections.clientManagement'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },

    // Mobile App Configuration - Site-level
    {
      id: 'mobile-app-customization',
      name: 'App Customization',
      keywords: 'app customization mobile branding',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-profile',
      name: 'Mobile Profile Settings',
      keywords: 'mobile profile settings user',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-channels',
      name: 'Mobile Channels',
      keywords: 'mobile channels communication',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-branding',
      name: 'Mobile Branding',
      keywords: 'mobile branding colors fonts logo',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-app-icon',
      name: 'App Icon',
      keywords: 'app icon home screen mobile',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-login-logo',
      name: 'Login Logo',
      keywords: 'login logo mobile branding',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-splash-light',
      name: 'Light Mode Splash Screen',
      keywords: 'light mode splash screen mobile',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },
    {
      id: 'mobile-splash-dark',
      name: 'Dark Mode Splash Screen',
      keywords: 'dark mode splash screen mobile',
      section: i18n.t('core:kbar.sections.mobileApp'),
      perform: () => buildSitePath(ROUTES.mobileSetup.path, siteId),
    },

    // Control Center - Corporate-level
    {
      id: 'control-communication-templates',
      name: 'Communication Templates',
      keywords: 'communication templates email messages',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-faqs',
      name: 'FAQs',
      keywords: 'faqs frequently asked questions help',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-integrations',
      name: 'Integrations',
      keywords: 'integrations third party apps connections',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-scheduler',
      name: 'Scheduler',
      keywords: 'scheduler session configurations rules',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-retail-sales',
      name: 'Retail Settings: Sales',
      keywords: 'retail settings sales packages preferences',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-retail-general',
      name: 'Retail Settings: General',
      keywords: 'retail settings general preferences',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },
    {
      id: 'control-brand-customization',
      name: 'Brand Customization',
      keywords: 'brand customization appearance preferences',
      section: i18n.t('core:kbar.sections.brandControl'),
      perform: () => buildCorporatePath(ROUTES.controlCenter.path),
    },

    // User Access Management - Corporate-level
    {
      id: 'user-add',
      name: 'Add New User',
      keywords: 'add new user staff employee access',
      section: i18n.t('core:kbar.sections.userManagement'),
      perform: () => buildCorporatePath(ROUTES.userAccess.path),
    },
    {
      id: 'user-list',
      name: 'Users List',
      keywords: 'users list staff employees manage',
      section: i18n.t('core:kbar.sections.userManagement'),
      perform: () => buildCorporatePath(ROUTES.userAccess.path),
    },
    {
      id: 'user-access-policies',
      name: 'Access Policies',
      keywords: 'access policies permissions roles',
      section: i18n.t('core:kbar.sections.userManagement'),
      perform: () => buildCorporatePath(`${ROUTES.userAccess.path}/access-policies`),
    },
    {
      id: 'user-location-groups',
      name: 'Location Groups',
      keywords: 'location groups access permissions',
      section: i18n.t('core:kbar.sections.userManagement'),
      perform: () => buildCorporatePath(`${ROUTES.userAccess.path}/location-groups`),
    },

    // Location Settings - Business Customization - Site-level
    {
      id: 'location-business-name',
      name: 'Business Name',
      keywords: 'business name company title location settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-business-description',
      name: 'Business Description',
      keywords: 'business description about company location settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-business-phone',
      name: 'Business Phone',
      keywords: 'business phone contact number location settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-business-address',
      name: 'Business Address',
      keywords: 'business address location street city location settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-revenue-period',
      name: 'Dashboard Revenue Period',
      keywords: 'dashboard revenue period financial settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-business-logo',
      name: 'Business Logo',
      keywords: 'business logo company branding image location settings',
      section: i18n.t('core:kbar.sections.businessSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },

    // Location Settings - Business Locations
    {
      id: 'location-business-locations',
      name: 'Business Locations',
      keywords: 'business locations sites branches multiple locations',
      section: i18n.t('core:kbar.sections.locationManagement'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-room-resource-config',
      name: 'Room Resource Configuration',
      keywords: 'room resource configuration equipment booking spaces',
      section: i18n.t('core:kbar.sections.locationManagement'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-business-hours',
      name: 'Business Hours',
      keywords: 'business hours opening times schedule location settings',
      section: i18n.t('core:kbar.sections.locationManagement'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-display-business-hours',
      name: 'Display Business Hours',
      keywords: 'display business hours show schedule public',
      section: i18n.t('core:kbar.sections.locationManagement'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },

    // Location Settings - Tools & Operations
    {
      id: 'location-tools-operations',
      name: 'Location Tools & Operations',
      keywords: 'location tools operations business management',
      section: i18n.t('core:kbar.sections.locationTools'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/location-tools`, siteId),
    },
    {
      id: 'location-front-desk-kiosk',
      name: 'Front Desk Kiosk',
      keywords: 'front desk kiosk display url beta version check-in',
      section: i18n.t('core:kbar.sections.locationTools'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/location-tools`, siteId),
    },
    {
      id: 'location-barcode-checkin',
      name: 'Barcode Check-in URL',
      keywords: 'barcode check-in checkin url scanner qr code',
      section: i18n.t('core:kbar.sections.locationTools'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/location-tools`, siteId),
    },

    // Location Check-in Settings - Section 5
    {
      id: 'location-enable-general-checkin',
      name: 'Enable General Check-in',
      keywords: 'enable general check-in checkin module turn on off',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-barcode-refresh-period',
      name: 'Barcode Check-in Screen Refresh Period',
      keywords: 'barcode check-in screen refresh period 10 seconds url',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-enable-mobile-checkin',
      name: 'Enable Client Check-in Mobile',
      keywords: 'enable client check-in mobile app checkin phone',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-enable-checkin-popups',
      name: 'Enable Check-in Pop-ups',
      keywords: 'enable check-in popups notifications alerts',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-enable-checkin-widget',
      name: 'Enable Check-in Widget',
      keywords: 'enable check-in widget display interface',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-enable-checkin-sounds',
      name: 'Enable Check-in Sound Alerts',
      keywords: 'enable check-in sound alerts audio notifications',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-contactless-checkin',
      name: 'Contactless Check-in',
      keywords: 'contactless check-in checkin touchless safety',
      section: i18n.t('core:kbar.sections.checkinSettings'),
      perform: () =>
        buildSitePath(`${ROUTES.settings.children.locationSettings.path}/check-in`, siteId),
    },
    {
      id: 'location-communication-templates',
      name: 'Communication Templates',
      keywords: 'communication templates messages email location',
      section: 'Location Tools',
      perform: () => buildSitePath('settings/location-settings', siteId),
    },
    {
      id: 'location-social-media',
      name: 'Social Media Settings',
      keywords: 'social media facebook instagram twitter location',
      section: 'Location Tools',
      perform: () => buildSitePath('settings/location-settings', siteId),
    },

    // Location Operations - Section 1
    {
      id: 'location-auto-logout',
      name: 'Auto Logout',
      keywords: 'auto logout automatic session timeout security',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-enable-signup',
      name: 'Enable Clients to Sign Up',
      keywords: 'enable clients sign up registration pathways allow',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-signup-fields-business',
      name: 'Sign Up Fields (Business Mode)',
      keywords: 'sign up fields business mode mandatory minimum registration',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-signup-fields-client',
      name: 'Sign Up Fields (Client Mode)',
      keywords: 'sign up fields client mode mandatory minimum registration',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-custom-client-fields',
      name: 'Custom Client Fields, Properties and Alerts',
      keywords: 'custom client fields properties alerts unique records',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-cancellation-reasons',
      name: 'Cancellation Reasons',
      keywords: 'cancellation reasons staff choose list cancel',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-lead-sources',
      name: 'Lead Sources',
      keywords: 'lead sources staff choose list marketing referral',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-policies',
      name: 'Policies',
      keywords: 'policies business terms conditions legal',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-suspension-reasons',
      name: 'Suspension Reasons',
      keywords: 'suspension reasons business suspend account',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-questionnaire-forms',
      name: 'Questionnaire Forms',
      keywords: 'questionnaire forms edit add survey forms',
      section: i18n.t('core:kbar.sections.locationOperations'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },

    // Location Operations - System Preferences (Section 2)
    {
      id: 'location-tabs-displayed',
      name: 'Tabs Displayed',
      keywords: 'tabs displayed web app accessible clients navigation',
      section: i18n.t('core:kbar.sections.systemPreferences'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-default-business-tab',
      name: 'Default Business Profile Tab',
      keywords: 'default business profile tab first page loads classes',
      section: i18n.t('core:kbar.sections.systemPreferences'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-available-client-tabs',
      name: 'Available Client Tabs',
      keywords: 'available client tabs profile page display',
      section: i18n.t('core:kbar.sections.systemPreferences'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-default-client-tab',
      name: 'Default Client Tab in Client File',
      keywords: 'default client tab file information profile',
      section: i18n.t('core:kbar.sections.systemPreferences'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },
    {
      id: 'location-show-business-info',
      name: 'Show Business Info on Client Dashboard',
      keywords: 'show business info client dashboard display',
      section: i18n.t('core:kbar.sections.systemPreferences'),
      perform: () =>
        buildSitePath(
          `${ROUTES.settings.children.locationSettings.path}/location-operations`,
          siteId
        ),
    },

    // Location Settings - Regional & Communication
    {
      id: 'location-digital-channels',
      name: 'Digital Channels',
      keywords: 'digital channels online communication jw player video',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-currency',
      name: 'Currency Settings',
      keywords: 'currency money pricing financial usd eur location settings',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-date-format',
      name: 'Date Format',
      keywords: 'date format dd mm yyyy regional settings',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-number-format',
      name: 'Number Format',
      keywords: 'number format decimals thousand separator regional',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-time-zone',
      name: 'Time Zone',
      keywords: 'time zone timezone utc daylight savings dst regional',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
    {
      id: 'location-time-format',
      name: 'Time Format',
      keywords: 'time format 12 hour 24 hour clock regional settings',
      section: i18n.t('core:kbar.sections.regionalSettings'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },

    // Quick Actions
    {
      id: 'quick-generate-report',
      name: 'Generate Report',
      keywords: 'generate report export data quick',
      section: i18n.t('core:kbar.sections.quickActions'),
      perform: () => buildCorporatePath(ROUTES.reports.path),
    },
    {
      id: 'quick-add-client',
      name: 'Add New Client',
      keywords: 'add new client member quick',
      section: i18n.t('core:kbar.sections.quickActions'),
      perform: () => buildSitePath(ROUTES.clients.path, siteId),
    },
    {
      id: 'quick-user-access',
      name: 'Manage User Access',
      keywords: 'manage user access permissions quick',
      section: i18n.t('core:kbar.sections.quickActions'),
      perform: () => buildCorporatePath(ROUTES.userAccess.path),
    },
    {
      id: 'quick-location-settings',
      name: 'Location Settings',
      keywords: 'location settings configuration business quick',
      section: i18n.t('core:kbar.sections.quickActions'),
      perform: () => buildSitePath(ROUTES.settings.children.locationSettings.path, siteId),
    },
  ]
}
